import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Eye, Search, Download, Loader2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataPagination } from "@/components/DataPagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WIDE_TABLE_SCROLL,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CampaignStatus, type CampaignStatusFilter, type RewardFilter, useListCampaignsByStatus } from "@/lib/hiring-api";
import { customFetch } from "@/lib/hiring-api/custom-fetch";
import { toast } from "sonner";

function StatusBadge({ status }: { status: CampaignStatus }) {
  switch (status) {
    case "active":
      return <Badge className="bg-green-500/10 text-green-700 hover:bg-green-500/20 border-green-500/20">Active</Badge>;
    case "draft":
      return <Badge className="bg-slate-500/10 text-slate-600 hover:bg-slate-500/20 border-slate-500/20">Draft</Badge>;
    case "paused":
      return <Badge variant="secondary" className="text-amber-700 bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20">Paused</Badge>;
    case "completed":
      return <Badge variant="outline" className="text-muted-foreground">Completed</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

type SortCol = "offer_amount" | "created_at";
type SortDir = "asc" | "desc";

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol | null; sortDir: SortDir }) {
  if (sortCol !== col) return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/60 inline" />;
  return sortDir === "asc"
    ? <ArrowUp className="ml-1 h-3.5 w-3.5 inline" />
    : <ArrowDown className="ml-1 h-3.5 w-3.5 inline" />;
}

const TABS: { value: CampaignStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
];

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<CampaignStatusFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isExporting, setIsExporting] = useState(false);
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [rewardFilter, setRewardFilter] = useState<RewardFilter>("all");

  function handleSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
    setPage(1);
  }

  // Debounce search by 300ms, reset page on change
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: campaignsData, isLoading } = useListCampaignsByStatus({
    status: activeTab,
    search,
    page,
    limit: pageSize,
    sortCol: sortCol ?? undefined,
    sortDir: sortCol ? sortDir : undefined,
    reward: rewardFilter,
  });
  const campaigns = campaignsData?.data ?? [];
  const total = campaignsData?.pagination?.total ?? 0;
  const totalPages = campaignsData?.pagination?.total_pages ?? Math.max(1, Math.ceil(total / pageSize));

  async function handleExport() {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      // Only apply filters if they're actually set
      if (activeTab !== "all") params.set("status", activeTab);
      if (search) params.set("search", search);
      if (rewardFilter === "reward") params.set("is_rewards_campaign", "true");
      if (rewardFilter === "non-reward") params.set("is_rewards_campaign", "false");

      const url = `/api/v1/hiring/campaigns/export${params.size > 0 ? `?${params.toString()}` : ""}`;

      // Use customFetch so the Authorization: Bearer token is automatically
      // attached (same as every other hiring API call)
      const blob = await customFetch<Blob>(url, { responseType: "blob" });

      const date = new Date().toISOString().slice(0, 10);
      const filename = `bmc-campaigns-${date}.csv`;

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);

      toast.success("Export downloaded");
    } catch (err: any) {
      toast.error(err?.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your active creator campaigns and pipeline.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export
          </Button>
          <Button asChild data-testid="btn-new-campaign">
            <Link to="/hiring/campaigns/new">
              <Plus className="mr-2 h-4 w-4" />
              New Campaign
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v as CampaignStatusFilter);
          setSearchInput("");
          setSearch("");
          setPage(1);
        }}>
          <TabsList>
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select value={rewardFilter} onValueChange={(v) => { setRewardFilter(v as RewardFilter); setPage(1); }}>
          <SelectTrigger className="h-9 w-40 text-sm">
            <SelectValue placeholder="Reward type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campaigns</SelectItem>
            <SelectItem value="reward">Reward only</SelectItem>
            <SelectItem value="non-reward">Non-reward only</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative ml-auto w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search brand or product..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        {/* Exclusive Campaigns tables keep the old scroll-sideways layout. */}
        <Table className={WIDE_TABLE_SCROLL}>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Brand & Product</TableHead>
              <TableHead
                className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                onClick={() => handleSort("offer_amount")}
              >
                $/Video <SortIcon col="offer_amount" sortCol={sortCol} sortDir={sortDir} />
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reward</TableHead>
              <TableHead
                className="cursor-pointer select-none hover:text-foreground whitespace-nowrap text-right"
                onClick={() => handleSort("offers_sent")}
              >
                Sent 
                {/* <SortIcon col="offers_sent" sortCol={sortCol} sortDir={sortDir} /> */}
              </TableHead>
              <TableHead className="text-right">Accepted</TableHead>
              <TableHead
                className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                onClick={() => handleSort("created_at")}
              >
                Created <SortIcon col="created_at" sortCol={sortCol} sortDir={sortDir} />
              </TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-48" /><Skeleton className="h-4 w-32 mt-1" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-16 rounded-full" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-5 w-8 ml-auto" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-5 w-8 ml-auto" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-16 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : campaigns?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                  No {activeTab === "all" ? "" : activeTab + " "}campaigns found.
                </TableCell>
              </TableRow>
            ) : (
              campaigns?.map((campaign) => (
                <TableRow key={campaign?.id} className="group">
                  <TableCell className="font-medium">
                    <div>{campaign?.brand_name}</div>
                    {campaign?.product_name && (
                      <div className="text-sm text-muted-foreground font-normal">
                        {campaign?.product_name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>${campaign?.offer_amount}</TableCell>
                  <TableCell>
                    <StatusBadge status={campaign?.status} />
                  </TableCell>
                  <TableCell>
                    {campaign?.is_rewards_campaign
                      ? <Badge className="bg-purple-500/10 text-purple-700 hover:bg-purple-500/20 border-purple-500/20">Reward</Badge>
                      : <span className="text-muted-foreground text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {campaign?.offers_sent}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {campaign?.offers_accepted}
                    {campaign?.max_spots ? <span className="text-muted-foreground font-normal text-xs ml-1">/ {campaign?.max_spots}</span> : null}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {campaign?.created_at ? new Date(campaign.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" asChild className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link to={`/hiring/campaigns/${campaign?.id}`} data-testid={`link-view-campaign-${campaign?.id}`}>
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DataPagination
        currentPage={page}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={total}
        pageSizeOptions={[10, 20, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize); setPage(1);
        }}
      />
    </div>
  );
}
